'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Bot,
    Shield,
    Zap,
    DollarSign,
    CheckCircle2,
    ArrowRight,
    Sparkles,
    Users,
    Lock,
    FileSignature,
    CreditCard,
    Eye,
    Building2,
    TrendingUp,
    Globe,
    BadgeCheck,
    Fingerprint,
    ShieldCheck,
    Brain,
    Handshake,
    Play,
    ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function VendorLandingPage() {
    const [activeFeature, setActiveFeature] = useState(0);

    const features = [
        {
            icon: Bot,
            title: 'AI Agent Discovery',
            description: 'Our AI agents intelligently match users with your services based on their needs, preferences, and context.',
        },
        {
            icon: Shield,
            title: 'Verified Users Only',
            description: 'Every LinkBox user is identity-verified. No bots, no fraud—only real customers ready to transact.',
        },
        {
            icon: FileSignature,
            title: 'Digital Signature Authorization',
            description: 'Every transaction requires explicit user signature. DocuSign-style consent ensures legal compliance.',
        },
        {
            icon: Lock,
            title: 'Secure Card Vault',
            description: 'Payment cards are stored in encrypted vaults. Vendors never see full card numbers—only authorized charges.',
        },
    ];

    const securitySteps = [
        {
            step: 1,
            title: 'User Requests Service',
            description: 'User asks their AI agent to find a service (e.g., "Find me a cheaper phone plan")',
            icon: Brain,
        },
        {
            step: 2,
            title: 'AI Discovers Vendors',
            description: 'Our matching engine finds verified vendors that match the user\'s criteria and budget',
            icon: Bot,
        },
        {
            step: 3,
            title: 'Terms Presented',
            description: 'Complete pricing, terms, and conditions are displayed for user review before any action',
            icon: Eye,
        },
        {
            step: 4,
            title: 'Signature Required',
            description: 'User must provide digital signature to authorize the transaction—no silent approvals',
            icon: Fingerprint,
        },
        {
            step: 5,
            title: 'Secure Fulfillment',
            description: 'Payment is processed through our secure gateway, and you receive funds directly',
            icon: ShieldCheck,
        },
    ];

    const stats = [
        { value: '$2M+', label: 'Monthly Transactions' },
        { value: '500+', label: 'Verified Vendors' },
        { value: '100%', label: 'User Consent Rate' },
        { value: '0.3%', label: 'Chargeback Rate' },
    ];

    const testimonials = [
        {
            quote: 'LinkBox\'s agentic commerce model brought us customers we never could have reached. The verified user base means virtually zero fraud.',
            author: 'Sarah Chen',
            role: 'VP Sales, TelcoPlus',
            avatar: '👩‍💼',
        },
        {
            quote: 'The signature requirement gives us legal protection we\'ve never had with other platforms. Every transaction is fully documented.',
            author: 'Marcus Johnson',
            role: 'CEO, StreamSync',
            avatar: '👨‍💻',
        },
        {
            quote: 'Integration took 2 hours. Now AI agents are driving 30% of our new subscriptions. Game changer.',
            author: 'Elena Rodriguez',
            role: 'CTO, FitnessPro',
            avatar: '👩‍🔬',
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
            {/* Navigation - Matching Main Site Branding */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link href="/" className="flex items-center gap-3 flex-shrink-0">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl flex-shrink-0"
                                unoptimized
                            />
                            <span className="text-xl font-bold whitespace-nowrap">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box AI</span>
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 whitespace-nowrap">
                                Vendors
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link href="/features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</Link>
                            <Link href="/ecosystem" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Ecosystem</Link>
                            <Link href="/messaging-integration.html" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Messaging</Link>
                            <Link href="/hardware" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Hardware</Link>
                            <Link href="/#pricing" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Pricing</Link>
                            <Link href="/hipaa" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">HIPAA</Link>
                            <Link href="/robotics" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Robotics</Link>
                            <Link href="/vendor" className="text-[#4ade80] transition-colors text-sm font-medium">Vendor</Link>
                            <Link href="/investors" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Investors</Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link
                                href="/vendor/login"
                                className="text-gray-300 hover:text-white transition-colors text-sm font-medium px-4 py-2"
                            >
                                Vendor Sign In
                            </Link>
                            <Link
                                href="/vendor/register"
                                className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-5 py-2 rounded-lg text-sm font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25"
                            >
                                Apply as Vendor
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-24 pb-24 overflow-hidden">
                {/* Animated background */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-emerald-500/10 to-transparent rounded-full blur-3xl animate-pulse" />
                    <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                </div>

                <div className="container max-w-6xl mx-auto px-4 relative">
                    <div className="text-center max-w-4xl mx-auto">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm mb-8">
                            <Zap className="w-4 h-4" />
                            The Future of Commerce is Agentic
                        </div>

                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                            Welcome to the{' '}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400">
                                Agentic Economy
                            </span>
                        </h1>

                        <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto leading-relaxed">
                            Join 500+ vendors who've discovered the power of AI-driven customer acquisition.
                            Our verified users trust their AI agents to find and transact with quality businesses like yours.
                        </p>

                        <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
                            <Link href="/vendor/register">
                                <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-lg px-8 h-14">
                                    Apply as a Vendor
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                            <Button size="lg" variant="ghost" className="border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 bg-transparent text-lg px-8 h-14">
                                <Play className="w-5 h-5 mr-2" />
                                Watch Demo
                            </Button>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
                            {stats.map((stat, i) => (
                                <div key={i} className="text-center">
                                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
                                        {stat.value}
                                    </div>
                                    <div className="text-sm text-slate-500">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* What is Agentic Commerce Section */}
            <section className="py-24 bg-slate-900/50">
                <div className="container max-w-6xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-white mb-4">
                            What is Agentic Commerce?
                        </h2>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                            The paradigm shift where AI agents act on behalf of users to discover, negotiate, and complete transactions—with full user consent and authorization.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="space-y-6">
                                {features.map((feature, i) => (
                                    <div
                                        key={i}
                                        className={cn(
                                            'p-6 rounded-2xl transition-all cursor-pointer border',
                                            activeFeature === i
                                                ? 'bg-gradient-to-r from-emerald-500/20 to-blue-500/20 border-emerald-500/30'
                                                : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                                        )}
                                        onClick={() => setActiveFeature(i)}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className={cn(
                                                'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0',
                                                activeFeature === i
                                                    ? 'bg-gradient-to-br from-emerald-500 to-blue-600'
                                                    : 'bg-slate-700'
                                            )}>
                                                <feature.icon className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                                                <p className="text-slate-400 text-sm">{feature.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="relative">
                            <div className="aspect-square rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 p-8 flex items-center justify-center">
                                <div className="text-center">
                                    <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center">
                                        <Bot className="w-16 h-16 text-emerald-400" />
                                    </div>
                                    <p className="text-white text-lg font-medium mb-2">AI Agent Says:</p>
                                    <p className="text-slate-400 italic">
                                        "I found 3 vendors matching your criteria. {features[activeFeature].title.toLowerCase()} ensures you're protected."
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Apparel Vertical Section */}
            <section className="py-24">
                <div className="container max-w-6xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <span className="inline-block rounded-full border border-pink-500/40 bg-pink-500/10 px-4 py-1 text-sm text-pink-300 mb-4">
                            New vendor vertical
                        </span>
                        <h2 className="text-4xl font-bold text-white mb-4">
                            Specialty Apparel Retailers
                        </h2>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                            Every LinkBox user has a personal AI assistant with an avatar,
                            a dressing room, and a wardrobe. Upload your designs and let
                            users dress their assistants in your line — draped
                            made-to-measure on every body.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6 mb-10">
                        <div className="p-6 rounded-2xl border border-slate-800 bg-slate-900/60">
                            <h3 className="text-white font-semibold mb-2">Upload your designs</h3>
                            <p className="text-slate-400 text-sm">
                                Product images, sewing patterns, or 3D assets — with a
                                self-serve retailer wardrobe to keep your line current and
                                retire what&apos;s sold out.
                            </p>
                        </div>
                        <div className="p-6 rounded-2xl border border-slate-800 bg-slate-900/60">
                            <h3 className="text-white font-semibold mb-2">The assistant tries it on</h3>
                            <p className="text-slate-400 text-sm">
                                We match a user&apos;s real closet into their avatar&apos;s wardrobe;
                                deciding what to wear, the assistant models outfit
                                combinations — their pieces and yours — on the user&apos;s own
                                avatar.
                            </p>
                        </div>
                        <div className="p-6 rounded-2xl border border-slate-800 bg-slate-900/60">
                            <h3 className="text-white font-semibold mb-2">Recommends and orders</h3>
                            <p className="text-slate-400 text-sm">
                                The assistant recommends pieces that complete an outfit and
                                places the order through the same vendor platform — orders,
                                agreements, and webhooks land in your systems.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center justify-center gap-4">
                        <Link
                            href="/apparel"
                            className="rounded-lg bg-pink-600 px-6 py-3 font-semibold text-white hover:bg-pink-500 transition-colors"
                        >
                            Explore apparel partnerships
                        </Link>
                        <Link
                            href="/apparel/apply"
                            className="rounded-lg border border-slate-700 px-6 py-3 font-semibold text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
                        >
                            Apply as an apparel retailer
                        </Link>
                    </div>
                </div>
            </section>

            {/* Security Process Section */}
            <section className="py-24 bg-slate-900/50">
                <div className="container max-w-6xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm mb-6">
                            <ShieldCheck className="w-4 h-4" />
                            Enterprise-Grade Security
                        </div>
                        <h2 className="text-4xl font-bold text-white mb-4">
                            Our Secure Transaction Flow
                        </h2>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                            Every transaction follows a rigorous 5-step process designed to protect both vendors and users. No silent approvals. No hidden fees. Full transparency.
                        </p>
                    </div>

                    <div className="relative">
                        {/* Connection line */}
                        <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500/50 via-cyan-500/50 to-blue-500/50 -translate-y-1/2" />

                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                            {securitySteps.map((step, i) => (
                                <div key={i} className="relative">
                                    <div className="bg-slate-800/80 backdrop-blur rounded-2xl p-6 border border-slate-700 hover:border-emerald-500/50 transition-all h-full">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg mb-4 relative z-10">
                                            {step.step}
                                        </div>
                                        <step.icon className="w-8 h-8 text-emerald-400 mb-3" />
                                        <h3 className="font-semibold text-white mb-2">{step.title}</h3>
                                        <p className="text-sm text-slate-400">{step.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Verified Users Matter */}
            <section className="py-24 bg-gradient-to-r from-emerald-950/50 to-blue-950/50">
                <div className="container max-w-6xl mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm mb-6">
                                <BadgeCheck className="w-4 h-4" />
                                Verified User Base
                            </div>
                            <h2 className="text-4xl font-bold text-white mb-6">
                                Why Our Users Are{' '}
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
                                    More Valuable
                                </span>
                            </h2>
                            <p className="text-lg text-slate-400 mb-8">
                                Every LinkBox user has completed identity verification and connected verified payment methods. This means:
                            </p>

                            <div className="space-y-4">
                                {[
                                    { icon: CheckCircle2, text: 'Real people with verified identities—no bots or fake accounts' },
                                    { icon: CreditCard, text: 'Pre-verified payment methods ready to transact' },
                                    { icon: TrendingUp, text: 'Higher conversion rates—these users are ready to buy' },
                                    { icon: Shield, text: 'Near-zero chargeback rates due to explicit consent' },
                                    { icon: Users, text: 'Quality over quantity—premium customer acquisition' },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                            <item.icon className="w-4 h-4 text-emerald-400" />
                                        </div>
                                        <span className="text-slate-300">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-slate-800/50 rounded-3xl p-8 border border-slate-700">
                            <h3 className="text-xl font-semibold text-white mb-6">Traditional vs. Agentic Commerce</h3>
                            <div className="space-y-4">
                                <ComparisonRow
                                    label="User Verification"
                                    traditional="Email only"
                                    agentic="Full identity + payment verified"
                                />
                                <ComparisonRow
                                    label="Transaction Consent"
                                    traditional="Checkbox"
                                    agentic="Digital signature required"
                                />
                                <ComparisonRow
                                    label="Chargeback Rate"
                                    traditional="1-3%"
                                    agentic="<0.3%"
                                />
                                <ComparisonRow
                                    label="Fraud Rate"
                                    traditional="2-5%"
                                    agentic="<0.1%"
                                />
                                <ComparisonRow
                                    label="Legal Protection"
                                    traditional="Terms page"
                                    agentic="Signed agreement w/ audit trail"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24">
                <div className="container max-w-6xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-white mb-4">
                            Trusted by Industry Leaders
                        </h2>
                        <p className="text-lg text-slate-400">
                            See why vendors are switching to agentic commerce
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {testimonials.map((testimonial, i) => (
                            <Card key={i} className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/30 transition-all">
                                <CardContent className="pt-6">
                                    <div className="text-4xl mb-4">{testimonial.avatar}</div>
                                    <p className="text-slate-300 mb-6 italic">"{testimonial.quote}"</p>
                                    <div>
                                        <div className="font-semibold text-white">{testimonial.author}</div>
                                        <div className="text-sm text-slate-500">{testimonial.role}</div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 to-blue-600/20" />
                <div className="container max-w-4xl mx-auto px-4 relative text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm mb-8">
                        <Handshake className="w-4 h-4" />
                        Join the Revolution
                    </div>

                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Enter the{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                            Agentic Economy?
                        </span>
                    </h2>

                    <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                        Start receiving qualified leads from AI agents today. Simple integration, no upfront costs, pay only for successful transactions.
                    </p>

                    <div className="flex flex-wrap items-center justify-center gap-4">
                        <Link href="/vendor/register">
                            <Button size="lg" className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 hover:from-[#86efac] hover:to-[#4ade80] text-lg px-8 h-14 font-semibold shadow-lg shadow-green-500/25">
                                Apply Now — It's Free
                                <ChevronRight className="w-5 h-5 ml-2" />
                            </Button>
                        </Link>
                        <Link href="mailto:support@linkboxai.com?subject=Vendor%20Sales%20Inquiry">
                            <Button size="lg" variant="ghost" className="border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 bg-transparent text-lg px-8 h-14">
                                Talk to Sales
                            </Button>
                        </Link>
                    </div>

                    <p className="text-sm text-slate-400 mt-6">
                        No credit card required • 24-hour approval • Dedicated onboarding support
                    </p>
                </div>
            </section>

            {/* Footer - Matching Main Site Branding */}
            <footer className="bg-gray-950 border-t border-gray-800 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl"
                                unoptimized
                            />
                            <span className="text-xl font-bold">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box AI</span>
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
                            <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                            <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
                            <Link href="/vendor" className="hover:text-white transition-colors">Vendor Portal</Link>
                            <Link href="/security" className="hover:text-white transition-colors">Security</Link>
                            <Link href="https://bb23llc.com/contact.html" className="hover:text-white transition-colors">Contact</Link>
                        </div>
                        <p className="text-sm text-gray-500">© 2026 BB23 LLC. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function ComparisonRow({
    label,
    traditional,
    agentic,
}: {
    label: string;
    traditional: string;
    agentic: string;
}) {
    return (
        <div className="grid grid-cols-3 gap-4 py-3 border-b border-slate-700 last:border-0">
            <div className="text-sm text-slate-500">{label}</div>
            <div className="text-sm text-slate-400 text-center">{traditional}</div>
            <div className="text-sm text-emerald-400 text-center font-medium">{agentic}</div>
        </div>
    );
}
